import { Button } from '@affine/component';
import { useConnectivityStatus } from '@affine/core/components/hooks/use-connectivity-status';
import { probeBackendLatencyNow } from '@affine/core/utils/backend-availability';
import { useI18n } from '@affine/i18n';
import { CloudWorkspaceIcon, NoNetworkIcon } from '@blocksuite/icons/rc';
import clsx from 'clsx';
import type { SVGProps } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { StreamLainPopover } from '../../../../components/dropdown-panel-chrome/radix-popover';
import { SIDEBAR_CREATE_BUTTON_SIZE } from '../sidebar-layout';
import * as styles from './sidebar-connectivity-status.css';
import {
  type BandwidthLevel,
  classifyMeasuredBandwidthLevel,
  classifyMeasuredConnectivityTone,
  classifyNetworkQuality,
  type ConnectivityTone,
  type NetworkInformationLike,
  type NetworkQuality,
  resolveConnectivityTone,
  resolveSyncTone,
} from './sidebar-connectivity-status-model';

type NetworkSignal = {
  browserOnline: boolean;
  quality: NetworkQuality;
  unavailableReason?: 'offline' | 'unknown';
};

type BackendProbeState = {
  available: boolean | null;
  bandwidthMbps: number | null;
  checking: boolean;
  latencyMs: number | null;
};

type ManualProbeState = 'idle' | 'checking';

type BackendProbeOptions = {
  measureBandwidth?: boolean;
  minDurationMs?: number;
};

const manualProbeMinimumDurationMs = 700;
const initialBackendProbeMinDelayMs = 2000;
const initialBackendProbeMaxDelayMs = 10000;
let initialBackendProbeQueued = false;
let initialBackendProbeCompleted = false;

const wait = (durationMs: number) =>
  new Promise<void>(resolve => {
    setTimeout(resolve, durationMs);
  });

const getConnection = (): NetworkInformationLike | null => {
  if (typeof navigator === 'undefined') return null;
  const typedNavigator = navigator as Navigator & {
    connection?: NetworkInformationLike;
    mozConnection?: NetworkInformationLike;
    webkitConnection?: NetworkInformationLike;
  };
  return (
    typedNavigator.connection ??
    typedNavigator.mozConnection ??
    typedNavigator.webkitConnection ??
    null
  );
};

const readNetworkSignal = (systemOnline: boolean): NetworkSignal => {
  if (!systemOnline) {
    return {
      browserOnline: false,
      quality: 'unknown',
      unavailableReason: 'offline',
    };
  }

  const connection = getConnection();
  if (!connection) {
    return {
      browserOnline: true,
      quality: 'unknown',
      unavailableReason: 'unknown',
    };
  }

  return {
    browserOnline: true,
    quality: classifyNetworkQuality(connection),
  };
};

const documentIsVisible = () =>
  typeof document === 'undefined' || document.visibilityState === 'visible';

const getInitialBackendProbeDelayMs = () =>
  initialBackendProbeMinDelayMs +
  Math.floor(
    Math.random() *
      (initialBackendProbeMaxDelayMs - initialBackendProbeMinDelayMs)
  );

// One startup probe keeps the dot honest without installing a heartbeat.
const queueInitialBackendProbe = () => {
  if (
    initialBackendProbeQueued ||
    initialBackendProbeCompleted ||
    typeof window === 'undefined'
  ) {
    return;
  }

  initialBackendProbeQueued = true;

  const scheduleProbe = () => {
    if (!documentIsVisible()) {
      if (typeof document === 'undefined') return;

      const waitForVisible = () => {
        if (!documentIsVisible()) return;
        document.removeEventListener('visibilitychange', waitForVisible);
        scheduleProbe();
      };
      document.addEventListener('visibilitychange', waitForVisible);
      return;
    }

    window.setTimeout(() => {
      if (!documentIsVisible()) {
        initialBackendProbeQueued = false;
        queueInitialBackendProbe();
        return;
      }

      initialBackendProbeCompleted = true;
      void probeBackendLatencyNow({ scheduleRetryOnFailure: false });
    }, getInitialBackendProbeDelayMs());
  };

  scheduleProbe();
};

const useNetworkSignal = (systemOnline: boolean) => {
  const [, setVersion] = useState(0);
  const refresh = useCallback(() => {
    setVersion(value => value + 1);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const connection = getConnection();

    connection?.addEventListener('change', refresh);
    window.addEventListener('online', refresh);
    window.addEventListener('offline', refresh);
    window.addEventListener('focus', refresh);

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', refresh);
    }

    refresh();

    return () => {
      connection?.removeEventListener('change', refresh);
      window.removeEventListener('online', refresh);
      window.removeEventListener('offline', refresh);
      window.removeEventListener('focus', refresh);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', refresh);
      }
    };
  }, [refresh]);

  return [readNetworkSignal(systemOnline), refresh] as const;
};

const useBackendLatencyProbe = (
  open: boolean,
  refreshNetworkSignal: () => void
) => {
  const [probe, setProbe] = useState<BackendProbeState>({
    available: null,
    bandwidthMbps: null,
    checking: false,
    latencyMs: null,
  });
  const inFlightPromiseRef = useRef<Promise<void> | null>(null);
  const runIdRef = useRef(0);

  const runProbe = useCallback(
    async (options: BackendProbeOptions = {}) => {
      const measureBandwidth = options.measureBandwidth ?? false;
      const minDurationMs = options.minDurationMs ?? 0;

      while (inFlightPromiseRef.current) {
        if (!measureBandwidth) return;
        await inFlightPromiseRef.current;
      }

      const runId = ++runIdRef.current;
      const startedAt = Date.now();
      refreshNetworkSignal();
      setProbe(current => ({ ...current, checking: true }));

      const probePromise = (async () => {
        try {
          const result = await probeBackendLatencyNow({
            measureBandwidth,
            scheduleRetryOnFailure: false,
          });
          const remainingDurationMs =
            minDurationMs - Math.max(0, Date.now() - startedAt);
          if (remainingDurationMs > 0) {
            await wait(remainingDurationMs);
          }
          if (runId === runIdRef.current) {
            setProbe(current => ({
              available: result.available,
              bandwidthMbps:
                result.bandwidthMbps ??
                (result.available ? current.bandwidthMbps : null),
              checking: false,
              latencyMs: result.latencyMs,
            }));
          }
        } finally {
          if (runId === runIdRef.current) {
            refreshNetworkSignal();
          }
        }
      })();

      inFlightPromiseRef.current = probePromise;
      try {
        await probePromise;
      } finally {
        if (inFlightPromiseRef.current === probePromise) {
          inFlightPromiseRef.current = null;
        }
      }
    },
    [refreshNetworkSignal]
  );

  useEffect(() => {
    if (!open || typeof window === 'undefined') return;

    void runProbe();

    return () => {
      runIdRef.current += 1;
      inFlightPromiseRef.current = null;
      setProbe(current => ({ ...current, checking: false }));
    };
  }, [open, runProbe]);

  useEffect(() => {
    if (!open || typeof window === 'undefined') return;
    if (probe.available === false || probe.checking) return;

    const timeoutId = window.setTimeout(() => {
      void runProbe();
    }, 2000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [open, probe.available, probe.checking, probe.latencyMs, runProbe]);

  return [probe, runProbe] as const;
};

const formatPingDescription = (
  probe: BackendProbeState,
  serverReachable: boolean,
  t: ReturnType<typeof useI18n>
) => {
  if (probe.checking && probe.latencyMs === null) {
    return t.t('com.streamlain.rootAppSidebar.connectivity.probe.checking');
  }

  if (!serverReachable) {
    return t.t('com.streamlain.rootAppSidebar.connectivity.probe.unreachable');
  }

  if (typeof probe.latencyMs === 'number') {
    return t.t('com.streamlain.rootAppSidebar.connectivity.probe.latency', {
      value: probe.latencyMs,
    });
  }

  return t.t('com.streamlain.rootAppSidebar.connectivity.probe.unknown');
};

const bandwidthBarsByLevel: Record<BandwidthLevel, number> = {
  unknown: 0,
  low: 1,
  medium: 2,
  high: 4,
};

const NetworkIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width="1em"
    height="1em"
    fill="none"
    style={{
      userSelect: 'none',
      flexShrink: 0,
    }}
    {...props}
  >
    <path
      fill="currentColor"
      fillRule="evenodd"
      d="M2.186 9.055a14.105 14.105 0 0 1 19.899 0 .75.75 0 0 1-1.06 1.06 12.605 12.605 0 0 0-17.779 0 .75.75 0 0 1-1.06-1.06Zm3.15 3.15a9.65 9.65 0 0 1 13.66 0 .75.75 0 0 1-1.061 1.06 8.15 8.15 0 0 0-11.539 0 .75.75 0 0 1-1.06-1.06Zm2.835 3.094a5.65 5.65 0 0 1 7.99 0 .75.75 0 0 1-1.061 1.061 4.15 4.15 0 0 0-5.869 0 .75.75 0 1 1-1.06-1.06Zm3.994 2.985a.75.75 0 1 0 0 1.5h.01a.75.75 0 0 0 0-1.5z"
      clipRule="evenodd"
    />
  </svg>
);

const ConnectivityStatusIcon = ({ tone }: { tone: ConnectivityTone }) =>
  tone === 'offline' ? (
    <NoNetworkIcon />
  ) : tone === 'paused' ? (
    <CloudWorkspaceIcon />
  ) : (
    <NetworkIcon />
  );

export const SidebarConnectivityStatus = ({
  placement = 'header',
  storageFull = false,
  onOpenPlans,
}: {
  placement?: 'header' | 'rail-avatar';
  /** The workspace storage is full and sync waits for room. */
  storageFull?: boolean;
  /** Offered to whoever can extend the plan while the storage is full. */
  onOpenPlans?: () => void;
}) => {
  const t = useI18n();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [manualProbeState, setManualProbeState] =
    useState<ManualProbeState>('idle');
  const connectivity = useConnectivityStatus();
  const [networkSignal, refreshNetworkSignal] = useNetworkSignal(
    connectivity.systemOnline
  );
  const [backendProbe, runBackendProbe] = useBackendLatencyProbe(
    open,
    refreshNetworkSignal
  );

  useEffect(() => {
    queueInitialBackendProbe();
  }, []);

  const effectiveBackendAvailable = connectivity.backendAvailable;
  const effectiveBrowserOnline =
    networkSignal.browserOnline || effectiveBackendAvailable;
  const serverReachable = effectiveBrowserOnline && effectiveBackendAvailable;
  const pingDescription = formatPingDescription(
    backendProbe,
    serverReachable,
    t
  );
  const measuredBandwidthLevel = classifyMeasuredBandwidthLevel(
    backendProbe.bandwidthMbps
  );
  const measuredTone = classifyMeasuredConnectivityTone(
    serverReachable ? backendProbe.latencyMs : null,
    serverReachable ? measuredBandwidthLevel : 'unknown'
  );
  const bandwidthLabel =
    typeof backendProbe.bandwidthMbps === 'number'
      ? t.t('com.streamlain.rootAppSidebar.connectivity.bandwidth.measured', {
          level: t.t(
            `com.streamlain.rootAppSidebar.connectivity.bandwidth.${measuredBandwidthLevel}`
          ),
          value: backendProbe.bandwidthMbps,
        })
      : t.t(
          `com.streamlain.rootAppSidebar.connectivity.bandwidth.${measuredBandwidthLevel}`
        );
  const activeBandwidthBars = bandwidthBarsByLevel[measuredBandwidthLevel];

  const tone = useMemo(
    () =>
      resolveSyncTone(
        resolveConnectivityTone(
          {
            backendAvailable: effectiveBackendAvailable,
            systemOnline: effectiveBrowserOnline,
          },
          networkSignal.quality,
          effectiveBrowserOnline,
          measuredTone
        ),
        storageFull
      ),
    [
      effectiveBackendAvailable,
      effectiveBrowserOnline,
      measuredTone,
      networkSignal.quality,
      storageFull,
    ]
  );

  const statusLabel = t.t(
    `com.streamlain.rootAppSidebar.connectivity.status.${tone}`
  );
  const tooltip = t.t('com.streamlain.rootAppSidebar.connectivity.tooltip', {
    status: statusLabel,
  });
  const description = t.t(
    `com.streamlain.rootAppSidebar.connectivity.description.${tone}`
  );

  const handleManualRetry = useCallback(async () => {
    if (manualProbeState === 'checking') return;
    setManualProbeState('checking');
    await runBackendProbe({
      measureBandwidth: true,
      minDurationMs: manualProbeMinimumDurationMs,
    });
    setManualProbeState('idle');
  }, [manualProbeState, runBackendProbe]);

  const handleRetryClick = useCallback(() => {
    void handleManualRetry();
  }, [handleManualRetry]);

  const retryLabel = t.t('com.streamlain.rootAppSidebar.connectivity.retry');
  const railAvatarPlacement = placement === 'rail-avatar';

  const popover = (
    <div className={styles.popoverContent}>
      <div className={styles.popoverHeader}>
        <span className={styles.statusIcon} data-tone={tone} aria-hidden="true">
          <ConnectivityStatusIcon tone={tone} />
        </span>
        <div className={styles.popoverTitle}>{statusLabel}</div>
      </div>
      <p className={styles.popoverDescription}>{description}</p>
      <div className={styles.statusRows}>
        <span className={styles.rowLabel}>
          {t.t('com.streamlain.rootAppSidebar.connectivity.browser')}
        </span>
        <span className={styles.rowValue}>
          {effectiveBrowserOnline
            ? t.t('com.streamlain.rootAppSidebar.connectivity.online')
            : t.t('com.streamlain.rootAppSidebar.connectivity.offline')}
        </span>
        <span className={styles.rowLabel}>
          {t.t('com.streamlain.rootAppSidebar.connectivity.server')}
        </span>
        <span className={styles.rowValue}>
          {serverReachable
            ? t.t('com.streamlain.rootAppSidebar.connectivity.reachable')
            : t.t('com.streamlain.rootAppSidebar.connectivity.unreachable')}
        </span>
        <span className={styles.rowLabel}>
          {t.t('com.streamlain.rootAppSidebar.connectivity.ping')}
        </span>
        <span className={styles.rowValue}>{pingDescription}</span>
        <span className={styles.rowLabel}>
          {t.t('com.streamlain.rootAppSidebar.connectivity.bandwidth')}
        </span>
        <span className={styles.bandwidthValue}>
          <span
            className={styles.bandwidthBars}
            data-level={measuredBandwidthLevel}
            aria-hidden="true"
          >
            {[0, 1, 2, 3].map(index => (
              <span
                key={index}
                className={styles.bandwidthBar}
                data-active={index < activeBandwidthBars}
                style={{ height: 5 + index * 3 }}
              />
            ))}
          </span>
          <span className={styles.bandwidthLabel}>{bandwidthLabel}</span>
        </span>
      </div>
      {tone === 'paused' && onOpenPlans ? (
        <Button
          block
          data-testid="app-sidebar-connectivity-open-plans"
          size="default"
          variant="primary"
          onClick={() => {
            setOpen(false);
            onOpenPlans();
          }}
        >
          {t.t('com.streamlain.rootAppSidebar.connectivity.open-plans')}
        </Button>
      ) : null}
      <Button
        className={styles.retryButton}
        contentClassName={styles.retryButtonContent}
        data-manual-state={manualProbeState}
        aria-busy={manualProbeState === 'checking'}
        block
        size="default"
        variant={tone === 'paused' && onOpenPlans ? 'secondary' : 'primary'}
        onClick={handleRetryClick}
      >
        <span className={styles.retrySpinner} aria-hidden="true" />
        <span className={styles.retryButtonLabel}>{retryLabel}</span>
      </Button>
    </div>
  );

  return (
    <StreamLainPopover
      triggerRef={triggerRef}
      content={popover}
      onOpenChange={setOpen}
      open={open}
      contentOptions={{
        align: railAvatarPlacement ? 'center' : 'end',
        side: railAvatarPlacement ? 'right' : 'bottom',
      }}
    >
      <Button
        ref={triggerRef}
        aria-label={tooltip}
        className={clsx(
          styles.statusButton,
          railAvatarPlacement && styles.railAvatarStatusButton
        )}
        contentClassName={clsx(
          styles.statusButtonContent,
          railAvatarPlacement && styles.railAvatarStatusButtonContent
        )}
        data-testid="app-sidebar-connectivity-status"
        size="custom"
        style={{
          width: railAvatarPlacement ? 24 : SIDEBAR_CREATE_BUTTON_SIZE,
          height: railAvatarPlacement ? 24 : SIDEBAR_CREATE_BUTTON_SIZE,
          padding: 0,
          borderRadius: railAvatarPlacement ? '50%' : 4,
        }}
        tooltip={tooltip}
        tooltipOptions={{
          side: railAvatarPlacement ? 'right' : 'bottom',
          align: railAvatarPlacement ? 'center' : 'end',
        }}
        variant="plain"
      >
        <span
          className={clsx(
            styles.statusIcon,
            railAvatarPlacement && styles.railAvatarStatusIcon
          )}
          data-tone={tone}
          aria-hidden="true"
        >
          <ConnectivityStatusIcon tone={tone} />
        </span>
      </Button>
    </StreamLainPopover>
  );
};
